/**
 * Supabase Service
 * Basic implementation for the feature-based architecture
 */

import { supabaseClient } from '@/shared/services/client';

class SupabaseService {
  public client = supabaseClient.getClient();

  // Auth methods
  async signUp(email: string, password: string) {
    return this.client.auth.signUp({ email, password });
  }

  async signIn(email: string, password: string) {
    return this.client.auth.signInWithPassword({ email, password });
  }

  async signOut() {
    return this.client.auth.signOut();
  }

  async getCurrentUser() {
    return this.client.auth.getUser();
  }

  onAuthStateChange(callback: (event: string, session: any) => void) {
    return this.client.auth.onAuthStateChange(callback);
  }

  // Generic database methods
  async fetch(table: string, options: any = {}) {
    let query = this.client.from(table).select(options.select || '*');

    if (options.eq) {
      Object.entries(options.eq).forEach(([key, value]) => {
        query = query.eq(key, value as any);
      });
    }

    if (options.order) {
      query = query.order(options.order.column, { ascending: options.order.ascending });
    }

    if (options.limit) {
      query = query.limit(options.limit);
    }

    return query;
  }

  async insert(table: string, data: any) {
    return this.client.from(table).insert(data);
  }

  async update(table: string, data: any, id: string) {
    return this.client.from(table).update(data).eq('id', id);
  }

  async delete(table: string, id: string) {
    return this.client.from(table).delete().eq('id', id);
  }

  // User profile methods
  async upsertProfile(profile: any) {
    return this.client.from('profiles').upsert(profile);
  }

  async getProfile(userId: string) {
    return this.client.from('profiles').select('*').eq('id', userId).single();
  }

  async updateProfile(userId: string, updates: any) {
    return this.client.from('profiles').update(updates).eq('id', userId);
  }

  async createProfile(profile: any) {
    return this.client.from('profiles').insert(profile);
  }

  // Profile mappings for Firebase UID integration
  async getProfileMapping(firebaseUid: string) {
    return this.client.from('profile_mappings').select('*').eq('firebase_uid', firebaseUid).single();
  }

  async createProfileMapping(firebaseUid: string, supabaseId: string) {
    return this.client.from('profile_mappings').insert({
      firebase_uid: firebaseUid,
      supabase_id: supabaseId,
      created_at: new Date().toISOString()
    });
  }

  // Client methods
  async createClient(client: any) {
    return this.client.from('clients').insert(client);
  }

  // Service methods
  async createService(service: any) {
    return this.client.from('services').insert(service);
  }

  // Health check
  isHealthy() {
    return !!this.client;
  }

  // Firebase integration methods
  setFirebaseTokenGetter(tokenGetter: () => Promise<string | null>) {
    (supabaseClient as any).setFirebaseTokenGetter(tokenGetter);
  }

  setFirebaseUidGetter(uidGetter: () => Promise<string | null>) {
    (supabaseClient as any).setFirebaseUidGetter(uidGetter);
  }
}

export const supabaseService = new SupabaseService();
export default supabaseService;